/**
 * Vello spacing + layout tokens (S055; IMPORT-0001 §3). Verbatim from `tokens/spacing.css`.
 *
 * A 4px base scale. `TAP_TARGET_MIN` (48px) is the accessibility floor for every interactive control
 * (AGENTS `apps/` a11y rule); a control smaller than 48px visually must still expand its touch target to
 * this minimum. Gutters and content-max are the responsive layout frame.
 */
/** The 4px spacing scale. Keys are the design-system step names (`space-1 … space-24`). */
export const SPACE = {
    1: 4,
    2: 8,
    3: 12,
    4: 16,
    5: 20,
    6: 24,
    8: 32,
    10: 40,
    12: 48,
    16: 64,
    20: 80,
    24: 96,
};
/**
 * The off-scale steps the Claude Design console (A08) draws between the 4px scale's steps (D547). They are NAMED so a
 * stylesheet never writes a bare 14px / 18px / 28px: `fine-6` (the scope pill's gap, the rail's gap), `fine-10` (a
 * menu item's padding, the brand row), `fine-14` (the KPI card's block padding, a dialog's gap), `fine-18` (the
 * page's column gap, the sidebar's top padding) and `fine-28` (the page's bottom padding, the dialog's padding).
 * Emitted as `--space-fine-<n>`.
 */
export const SPACE_FINE = {
    6: 6,
    10: 10,
    14: 14,
    18: 18,
    28: 28,
};
/** Layout frame tokens (px). */
export const LAYOUT = {
    pageGutterMobile: 20,
    pageGutterDesktop: 64,
    contentMax: 1200,
    /* D941: the three columns of a page of the manual (Claude Design, "Manual do Vello" 1A). */
    manualSidebar: 288,
    manualContent: 760,
    manualToc: 224,
};
/** The minimum interactive target size, in px. Never render an interactive control below this touch size. */
export const TAP_TARGET_MIN = 48;
/**
 * The drawn (visual) size of the dense web controls, in px (W5-THEME-TARGETS, D387; owner D381 #6 "no exception to
 * 48 px, the dense look stays"). Each is the size the Claude Design draws the control at; the control's own box — its
 * hit area — is never smaller than {@link TAP_TARGET_MIN}, and the visual is painted inside it (`@vello/web-theme`
 * paints it on the control's `::before`, or on an inner element such as the small button's pill). Every value is
 * below the floor on purpose: a control drawn at 48 px or more needs no separate visual.
 */
export const CONTROL_VISUAL = {
    /** The column-picker checkbox square. */
    check: 18,
    /** The sidebar collapse toggle (a circle on the sidebar edge). */
    toggle: 24,
    /** Reorder arrows and the toast close control. */
    arrow: 26,
    /** The sidebar sign-out icon. */
    signout: 30,
    /** The toast undo pill. */
    undo: 32,
    /** A navigation row at depth 2. */
    navDeep: 32,
    /** A navigation row at depth 1. */
    navSub: 34,
    /** The small row/toolbar pill button (`Button size="sm"`). */
    pill: 34,
    /** Top-bar controls (header button, search trigger, bell), reason chips and the icon-button box. */
    header: 36,
    /** A navigation row at depth 0. */
    nav: 38,
};
/**
 * Control sizes ABOVE the 48 px floor (V-LIB-FORMS, D508). `cta` is the large call-to-action (`Button size="lg"`): the
 * partner desk's confirm and sign-in buttons were drawn at 54 px with an inline style; this is the token for it.
 * Kept apart from {@link CONTROL_VISUAL}, whose every value is below the floor on purpose.
 */
export const CONTROL_LARGE = {
    cta: 54,
};
//# sourceMappingURL=space.js.map